import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { THEME } from '../../utils/theme';

const HelpCenterModal = ({ onClose, onStartTour }) => {
  const [activeTab, setActiveTab] = useState('overview');

  const tabs = [
    { id: 'overview', label: 'Overview', icon: '🎮' },
    { id: 'squads', label: 'Squads', icon: '👥' },
    { id: 'games', label: 'Games', icon: '🎯' },
    { id: 'discover', label: 'Discover', icon: '🔍' },
    { id: 'keyboard', label: 'Shortcuts', icon: '⌨️' },
  ];

  const shortcuts = [
    { keys: ['H'], desc: 'Open Help Center' },
    { keys: ['?'], desc: 'Show keyboard shortcuts' },
    { keys: ['W'], desc: 'What Should We Play? (Smart suggestions)' },
    { keys: ['R'], desc: 'Random Game Night picker' },
    { keys: ['/', 'S'], desc: 'Focus search' },
    { keys: ['F'], desc: 'Open filters' },
    { keys: ['D'], desc: 'Stats dashboard' },
    { keys: ['L'], desc: 'Toggle LFG mode' },
    { keys: ['⌘K', 'Ctrl+K'], desc: 'Command palette' },
    { keys: ['Esc'], desc: 'Close modals / Clear selection' },
  ];

  const statuses = [
    { value: 'played', label: 'Played', color: THEME.success, icon: '✓', desc: 'Games you\'ve played together. Shows first when browsing.' },
    { value: 'wtp', label: 'Want to Play', color: THEME.warning, icon: '📋', desc: 'Your backlog! Games you want to try. Shows in LFG mode.' },
    { value: 'shelved', label: 'Shelved', color: THEME.textSecondary, icon: '📦', desc: 'Set aside for now. Hidden from random picker.' },
    { value: 'wtf', label: 'What\'s This?', color: THEME.pink, icon: '❓', desc: 'Don\'t recognize it. Mark to investigate later.' },
    { value: 'never', label: 'Never Again', color: THEME.danger, icon: '🚫', desc: 'Never want to play. Hidden from ALL views except "Never" tab.' },
    { value: 'unplayed', label: 'Unplayed', color: THEME.textMuted, icon: '⏸', desc: 'Haven\'t tried it yet. Default for new games.' },
  ];

  const discoverFeatures = [
    { name: 'Smart Suggestions', icon: '🧠', desc: 'AI-powered recommendations based on ratings and compatibility.' },
    { name: 'Hidden Gems', icon: '💎', desc: 'Highly-rated games with low playtime.' },
    { name: 'Game Night Scheduler', icon: '📅', desc: 'Schedule future game nights with Discord reminders!' },
    { name: 'Recent Sessions', icon: '🕐', desc: 'Last 10 played games. Quick access to recent favorites.' },
    { name: 'Top 10 (30 Days)', icon: '🔥', desc: 'Most played games in the last month.' },
    { name: 'Rate Unrated', icon: '⭐', desc: 'Games played but not rated. Improve recommendations!' },
    { name: 'Random Picker', icon: '🎲', desc: 'Can\'t decide? Let fate choose! Filter by mood.' },
  ];

  const squadFeatures = [
    { name: 'Squad Selector', icon: '👥', desc: 'Switch between your squads using the dropdown in the header. Each squad has its own library view.' },
    { name: 'Squad Chat', icon: '💬', desc: 'Click your squad name to open squad chat. Discuss what to play with your team!' },
    { name: 'Member List', icon: '👤', desc: 'See all squad members, their status, and theme colors. Founders have special badges.' },
    { name: 'Kick Voting', icon: '🗳️', desc: 'Vote to remove members. Requires 2 votes in most squads. Founders can\'t be kicked from the founder squad.' },
    { name: 'Invites', icon: '✉️', desc: 'Create invite codes in Settings → Invites. Codes expire in 72 hours. Invite to a specific squad or just to the platform.' },
  ];

  const gameFeatures = [
    { name: 'Status', icon: '📊', desc: 'Set your status (Played, WTP, etc.) from the dropdown on each game card or in the detail modal.' },
    { name: 'Rating', icon: '⭐', desc: 'Click a game card\'s rating area to quick-rate (1-10). Or open details for full rating with notes.' },
    { name: 'Play Queue', icon: '📋', desc: 'Add games to your queue with "+ Add to Queue". Your #1 is your "Want to Play Next". Drag to reorder!' },
    { name: 'Log Sessions', icon: '🎮', desc: 'Track when you played and for how long. Opens a recap screen with your stats!' },
    { name: 'Tags', icon: '🏷️', desc: 'Add custom tags to games (e.g., #chill, #competitive). Tags sync across your squad.' },
    { name: 'HowLongToBeat', icon: '⏱️', desc: 'See estimated completion times in the game detail modal.' },
    { name: 'Achievements', icon: '🏆', desc: 'View your Steam achievement progress with unlock status and global rarity.' },
    { name: 'Game Chat', icon: '💬', desc: 'Each game has its own chat thread. Discuss strategies, memories, or just chat!' },
  ];

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(5,5,10,0.88)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100, backdropFilter: 'blur(32px) saturate(180%)', WebkitBackdropFilter: 'blur(32px) saturate(180%)' }} onClick={onClose}>
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.2 }} style={{ background: `linear-gradient(165deg, ${THEME.bgCard} 0%, ${THEME.bgElevated} 100%)`, borderRadius: '24px', width: '95%', maxWidth: '700px', maxHeight: '85vh', display: 'flex', flexDirection: 'column', border: `1px solid ${THEME.glassBorder}`, boxShadow: THEME.modalShadow, overflow: 'hidden' }} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={{ padding: '20px 24px', borderBottom: `1px solid ${THEME.glassBorder}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: `linear-gradient(180deg, ${THEME.bgElevated}80 0%, transparent 100%)` }}>
          <div>
            <h2 style={{ margin: 0, color: THEME.text, fontSize: '22px', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span style={{ fontSize: '26px' }}>📚</span> Help Center
            </h2>
            <p style={{ margin: '4px 0 0', color: THEME.textMuted, fontSize: '13px' }}>Everything you need to know about jasons.fun</p>
          </div>
          <motion.button whileHover={{ scale: 1.1, backgroundColor: THEME.bgActive }} whileTap={{ scale: 0.95 }} onClick={onClose} style={{ background: THEME.bgHover, border: `1px solid ${THEME.glassBorder}`, color: THEME.textMuted, fontSize: '20px', cursor: 'pointer', borderRadius: '10px', width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</motion.button>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: '4px', padding: '12px 20px', borderBottom: `1px solid ${THEME.border}`, background: THEME.bg }}>
          {tabs.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={{
              flex: 1, padding: '10px 12px', background: activeTab === tab.id ? THEME.bgElevated : 'transparent',
              border: activeTab === tab.id ? `1px solid ${THEME.border}` : '1px solid transparent',
              borderRadius: '10px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
              color: activeTab === tab.id ? THEME.text : THEME.textMuted, fontSize: '13px', fontWeight: activeTab === tab.id ? '600' : '500',
              transition: 'all 0.2s'
            }}>
              <span>{tab.icon}</span>
              <span>{tab.label}</span>
            </button>
          ))}
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflow: 'auto', padding: '20px 24px' }}>
          {activeTab === 'overview' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <div style={{ background: `linear-gradient(135deg, ${THEME.omega}15, ${THEME.supe}15)`, borderRadius: '14px', padding: '20px', border: `1px solid ${THEME.border}` }}>
                <h3 style={{ margin: '0 0 12px', color: THEME.text, fontSize: '16px', fontWeight: '600' }}>Welcome to jasons.fun!</h3>
                <p style={{ margin: 0, color: THEME.textSecondary, fontSize: '14px', lineHeight: '1.6' }}>
                  Your co-op gaming compendium for tracking multiplayer games. Rate games, plan game nights, discover hidden gems, and decide what to play together!
                </p>
              </div>

              <div>
                <h4 style={{ margin: '0 0 12px', color: THEME.text, fontSize: '14px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Quick Start</h4>
                <div style={{ display: 'grid', gap: '10px' }}>
                  {[
                    { icon: '👥', title: 'Join a Squad', desc: 'Use an invite code to join a squad and see shared game libraries.' },
                    { icon: '📊', title: 'Set Your Statuses', desc: 'Mark games: Played, WTP (backlog), Shelved, WTF, or Never Again.' },
                    { icon: '⭐', title: 'Rate Your Games', desc: 'Rate 1-10 and add notes. Click the rating area on any card!' },
                    { icon: '📋', title: 'Build Your Queue', desc: 'Add games to your queue. #1 is your "Want to Play Next"!' },
                    { icon: '🎲', title: 'Pick a Game', desc: 'Press R for random picker, W for smart suggestions.' },
                    { icon: '⚙️', title: 'Customize Settings', desc: 'Click ⚙️ for profile, Discord webhooks, theme & invites.' },
                  ].map((item, idx) => (
                    <div key={idx} style={{ display: 'flex', gap: '12px', padding: '12px', background: THEME.bg, borderRadius: '10px', border: `1px solid ${THEME.border}` }}>
                      <span style={{ fontSize: '20px', flexShrink: 0 }}>{item.icon}</span>
                      <div>
                        <div style={{ color: THEME.text, fontWeight: '600', fontSize: '14px' }}>{item.title}</div>
                        <div style={{ color: THEME.textMuted, fontSize: '13px', marginTop: '2px' }}>{item.desc}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <button onClick={() => { onClose(); onStartTour(); }} style={{
                width: '100%', padding: '14px', background: `linear-gradient(135deg, ${THEME.omega}, ${THEME.supe})`,
                border: 'none', borderRadius: '12px', color: '#000', fontWeight: '700', fontSize: '15px', cursor: 'pointer'
              }}>
                🎬 Start Interactive Tour
              </button>
            </div>
          )}

          {activeTab === 'squads' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <p style={{ margin: '0 0 8px', color: THEME.textSecondary, fontSize: '14px' }}>
                Squads are groups of players who share their game library. You can be in multiple squads!
              </p>
              {squadFeatures.map((f, idx) => (
                <div key={idx} style={{ display: 'flex', gap: '12px', padding: '14px', background: THEME.bg, borderRadius: '12px', border: `1px solid ${THEME.border}` }}>
                  <span style={{ fontSize: '22px', flexShrink: 0 }}>{f.icon}</span>
                  <div>
                    <div style={{ color: THEME.text, fontWeight: '600', fontSize: '14px', marginBottom: '4px' }}>{f.name}</div>
                    <div style={{ color: THEME.textMuted, fontSize: '13px', lineHeight: '1.5' }}>{f.desc}</div>
                  </div>
                </div>
              ))}
              <div style={{ background: `${THEME.info}15`, borderRadius: '10px', padding: '14px', border: `1px solid ${THEME.info}30`, marginTop: '8px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                  <span style={{ fontSize: '16px' }}>💡</span>
                  <span style={{ color: THEME.info, fontWeight: '600', fontSize: '13px' }}>Pro Tip</span>
                </div>
                <p style={{ margin: 0, color: THEME.textSecondary, fontSize: '13px' }}>
                  The "Founding Fathers" squad is special - it's where the original creators hang out. Founders get special badges and can't be kicked from it!
                </p>
              </div>
            </div>
          )}

          {activeTab === 'games' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <p style={{ margin: '0 0 8px', color: THEME.textSecondary, fontSize: '14px' }}>
                Everything you can do with games in your library:
              </p>
              <div style={{ display: 'grid', gap: '10px', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))' }}>
                {gameFeatures.map((f, idx) => (
                  <div key={idx} style={{ padding: '12px', background: THEME.bg, borderRadius: '10px', border: `1px solid ${THEME.border}` }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                      <span style={{ fontSize: '18px' }}>{f.icon}</span>
                      <span style={{ color: THEME.text, fontWeight: '600', fontSize: '13px' }}>{f.name}</span>
                    </div>
                    <p style={{ margin: 0, color: THEME.textMuted, fontSize: '12px', lineHeight: '1.5' }}>{f.desc}</p>
                  </div>
                ))}
              </div>
              <div style={{ marginTop: '12px' }}>
                <h4 style={{ margin: '0 0 10px', color: THEME.text, fontSize: '13px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Game Statuses</h4>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {statuses.map((s, idx) => (
                    <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 12px', background: `${s.color}15`, borderRadius: '8px', border: `1px solid ${s.color}30` }}>
                      <span style={{ fontSize: '14px' }}>{s.icon}</span>
                      <span style={{ color: s.color, fontWeight: '600', fontSize: '12px' }}>{s.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'discover' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <p style={{ margin: '0 0 8px', color: THEME.textSecondary, fontSize: '14px' }}>
                Discover features help you find the perfect game to play. Access them from the Discover dropdown in the header.
              </p>
              <div style={{ display: 'grid', gap: '10px', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))' }}>
                {discoverFeatures.map((f, idx) => (
                  <div key={idx} style={{ padding: '14px', background: THEME.bg, borderRadius: '12px', border: `1px solid ${THEME.border}` }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                      <span style={{ fontSize: '22px' }}>{f.icon}</span>
                      <span style={{ color: THEME.text, fontWeight: '600', fontSize: '14px' }}>{f.name}</span>
                    </div>
                    <p style={{ margin: 0, color: THEME.textMuted, fontSize: '13px', lineHeight: '1.5' }}>{f.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'keyboard' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <p style={{ margin: '0 0 8px', color: THEME.textSecondary, fontSize: '14px' }}>
                Power users rejoice! These keyboard shortcuts help you navigate faster.
              </p>
              {shortcuts.map((s, idx) => (
                <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 14px', background: THEME.bg, borderRadius: '10px', border: `1px solid ${THEME.border}` }}>
                  <span style={{ color: THEME.text, fontSize: '14px' }}>{s.desc}</span>
                  <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                    {s.keys.map((key, i) => (
                      <span key={i} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <kbd style={{
                          background: THEME.bgElevated, color: THEME.text, padding: '6px 12px', borderRadius: '8px',
                          fontSize: '13px', fontWeight: '700', fontFamily: 'monospace', border: `1px solid ${THEME.border}`,
                          boxShadow: `0 2px 0 ${THEME.border}`
                        }}>{key}</kbd>
                        {i < s.keys.length - 1 && <span style={{ color: THEME.textDim, fontSize: '12px' }}>or</span>}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default HelpCenterModal;
